<template>
    <v-layout align-center justify-center class="upload_bg">

        <!-- GLOBAL APP BAR HEADER -->
        <v-toolbar fixed flat class="transparent white--text">
            <span>Update Company Logo</span>
            <v-spacer></v-spacer>
            <v-toolbar-items>       
                <v-btn dark flat @click="closeDialog">Close</v-btn>
            </v-toolbar-items>
        </v-toolbar>

        <!-- GLOBAL ALERTS CONTAINER -->
        <v-alert v-if="error" :value="true" type="error" icon="error" dismissible class="mb-4">
            Error encountered. Please contact support. Error details: {{ errorMessage }}
        </v-alert>  
        
        <!-- CONTAINER CONTENT BOX -->
        <v-flex xs12 sm7 :class="isMobile ? 'content-container-mobile' : ''">  
            <div class="text-xs-center" :class="isMobile ? 'content-container-main-mobile' : ''">
                <!-- DRAG AND DROP FILE COMPONENT LAYER -->
                <div v-show="showUpload" class="text-xs-center">
                    <vue-dropzone ref="myVueDropzone" 
                        class="dropzone-component"
                        id="dropzone" 
                        :options="dropzoneOptions"
                        v-on:vdropzone-success="uploadSuccess"
                        v-on:vdropzone-file-added="fileAdded"
                    ></vue-dropzone>
                </div>
            </div>
        </v-flex> 

    </v-layout>
</template>

<script>
import axios from 'axios'
import companyApi from '@/api/company'
import vue2Dropzone from 'vue2-dropzone'
import 'vue2-dropzone/dist/vue2Dropzone.min.css'
import pageKeys from '@/common/pagekeys'

export default {
    name: 'EditCompanyLogo',
    components: {
        vueDropzone: vue2Dropzone
    },
    props: {
        isMobile: {
            type: Boolean,
            default: false
        }
    },
    data () {
        return {
            companyId: null,
            loading: false,   
            showUpload: true,      
            error: false,
            errorMessage: ''
        }
    },
    methods: {
        fileAdded(file) {
            this.loading = true;
            this.error = false;
            this.errorMessage = '';
        },
        async uploadSuccess(file, response) {
            this.error = false;
            try {
                if (response && response.fileName) {
                    const fileName = response.fileName;
                    const data = { "logoFileName": fileName };
                    const result = await companyApi.update(data);

                    if (result && result.status === 200) {
                        this.$store.commit('setCompanyLogo', fileName); 
                        this.$root.$emit('updateHeaderCompanyLogo', 'updateHeaderCompanyLogoHandler');
                        this.$refs.myVueDropzone.removeAllFiles();
                        this.$emit('send', null);
                        this.$emit('update:dialogEditLogo', false);
                    } else {
                        throw new Error("Failed to save the updated company logo profile record.");
                    }
                } else {
                    throw new Error("Invalid response format received from upload system server.");
                }
                this.loading = false;
            } catch (error) {
                this.loading = false;
                this.error = true;
                this.errorMessage = error.message || error;
                this.$refs.myVueDropzone.removeAllFiles();
            }
        },
        closeDialog() {
            this.showUpload = true;
            this.error = false;
            this.errorMessage = '';
            this.$emit('update:dialogEditLogo', false);   
        }
    },        
    created() {
        this.companyId = this.$store.state.companyId;
    },
    computed: {
        dropzoneOptions() {    
            return {   
                url: `${axios.defaults.baseURL}Image/upload/company_logo`,
                headers: { 
                    "Authorization": `Bearer ${localStorage.token}`,
                    "page_key": pageKeys.company_account,
                    "session_id": localStorage.sessionId,
                    "source": "Customer"
                },
                addRemoveLinks: false, 
                
                // --- STANDARDIZED DICTIONARY VALIDATION STRINGS ---
                dictInvalidFileType: "Unsupported Format. Please select a valid logo image.",
                dictFileTooBig: "File size exceeds 5MB limit.",
                dictMaxFilesExceeded: "You can only process one file asset at a time.",
                dictDefaultMessage: this.dropDescription,
                
                maxFiles: 1,
                acceptedFiles: 'image/*', 
                maxFilesize: 5.0, //5MB

                // INTERCEPT FILE FORMAT REJECTIONS AND BIND PURGE HANDLERS TO EXPLICIT ELEMENT LAYERS
                init: function() {
                    this.on("error", function(file, message) {
                        const errorEl = file.previewElement.querySelector('.dz-error-message');
                        if (errorEl) {
                            errorEl.addEventListener('click', () => {
                                this.removeFile(file); // Purges file instantly on text click
                            });
                        }
                    });
                }
            }
        },
        dropDescription() {
            return `<p class='subheading'><u>${this.isMobile ? 'Tap to browse gallery' : 'Drop your photo here or click to browse gallery'}</u></p><br>
                    <p class='subheading'>Maximum 5 MB</p>`
        }
    }
}
</script>

<style scoped>
.upload_bg {
    height: 100%;
    background-position: center;
    background-repeat: no-repeat;
    background-size: cover;
    background-color: rgba(0, 0, 0, 0.9)
}
.content-container-mobile {
    position: relative !important;
    height: 100% !important;
}
.content-container-main-mobile {
    position: absolute; 
    top: 20%; 
    width: 100%;
}
.dropzone-component {
    height: 300px !important;
}

/* ==========================================================================
   DROPZONE VUE-DEEP STYLING OVERRIDES (CENTERED TEXT ONLY MINIMALIST LOOK)
   ========================================================================== */
::v-deep #dropzone {
    border: 2px dashed rgba(255, 255, 255, 0.3) !important;
    border-radius: 8px;
    background-color: rgba(0, 0, 0, 0.4) !important;
    color: white !important;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    margin: 10px;
}
::v-deep #dropzone .dz-preview {
    background: #1e1e1e !important; 
    border: 1px solid rgba(255, 255, 255, 0.15) !important;
    border-radius: 8px !important;
    padding: 20px !important;
    width: 280px !important; 
    position: absolute !important;
    left: 50% !important;
    top: 50% !important;
    transform: translate(-50%, -50%) !important;
    margin: 0 !important;
    box-sizing: border-box !important;
    box-shadow: 0px 4px 20px rgba(0, 0, 0, 0.5) !important;
}
::v-deep #dropzone .dz-preview .dz-image {
    border-radius: 6px !important;
    margin: 0 auto 15px auto !important;
    display: block !important;
    width: 120px !important;
    height: 120px !important;
}
::v-deep #dropzone .dz-preview.dz-error .dz-image {
    display: none !important;
}
::v-deep #dropzone .dz-preview .dz-details {
    color: #FFF !important;
    background: transparent !important;
    opacity: 1 !important;
    position: static !important;
    padding: 0 !important;
    text-align: center !important;
    margin-bottom: 12px !important;
}
::v-deep #dropzone .dz-preview .dz-error-message {
    display: none !important; 
}
::v-deep #dropzone .dz-preview.dz-error .dz-error-message {
    position: static !important; 
    opacity: 1 !important;
    display: block !important;
    background: #FF5252 !important;
    color: white !important;
    border-radius: 4px !important;
    padding: 12px 16px !important;
    font-size: 13px !important;
    font-weight: bold !important;
    text-align: center !important;
    margin: 5px 0 0 0 !important;
    width: 100% !important;
    box-sizing: border-box !important;
    cursor: pointer !important;
    transition: background-color 0.2s ease;
}
::v-deep #dropzone .dz-preview.dz-error .dz-error-message:hover {
    background-color: #D32F2F !important;
}
::v-deep #dropzone .dz-preview .dz-error-message:after {
    display: none !important;
}
::v-deep #dropzone .dz-preview .dz-success-mark,
::v-deep #dropzone .dz-preview .dz-error-mark,
::v-deep #dropzone .dz-preview .dz-remove {
    display: none !important;
}
</style>
